/**
 * Course Management Hooks
 * Coláiste Lurgan Management System - Course data management
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../lib/api';
import { queryKeys, invalidateQueries } from '../lib/queryClient';

// Get all courses with filters
export function useCourses(filters = {}, options = {}) {
  return useQuery({
    queryKey: queryKeys.courses.list(filters),
    queryFn: () => api.courses.getAll(filters),
    ...options,
  });
}

// Get single course
export function useCourse(courseId, options = {}) {
  return useQuery({
    queryKey: queryKeys.courses.detail(courseId),
    queryFn: () => api.courses.getById(courseId),
    enabled: !!courseId,
    ...options,
  });
}

// Get course enrollment details
export function useCourseEnrollment(courseId, options = {}) {
  return useQuery({
    queryKey: queryKeys.courses.enrollment(courseId),
    queryFn: () => api.courses.getEnrollmentDetails(courseId),
    enabled: !!courseId,
    staleTime: 2 * 60 * 1000, // 2 minutes - enrollment changes frequently
    ...options,
  });
}

// Get course analytics
export function useCourseAnalytics(options = {}) {
  return useQuery({
    queryKey: queryKeys.courses.analytics(),
    queryFn: api.courses.getAnalytics,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

// Create course mutation
export function useCreateCourse(options = {}) {
  return useMutation({
    mutationFn: api.courses.create,
    onSuccess: () => {
      invalidateQueries.courses();
    },
    ...options,
  });
}

// Update course mutation
export function useUpdateCourse(options = {}) {
  return useMutation({
    mutationFn: ({ id, ...data }) => api.courses.update(id, data),
    onSuccess: (updatedCourse) => {
      invalidateQueries.course(updatedCourse.id);
    },
    ...options,
  });
}

// Delete course mutation
export function useDeleteCourse(options = {}) {
  return useMutation({
    mutationFn: api.courses.delete,
    onSuccess: () => {
      invalidateQueries.courses();
    },
    ...options,
  });
}

// Duplicate course mutation
export function useDuplicateCourse(options = {}) {
  return useMutation({
    mutationFn: ({ id, ...data }) => api.courses.duplicate(id, data),
    onSuccess: () => {
      invalidateQueries.courses();
    },
    ...options,
  });
}

// Archive course roster mutation
export function useArchiveCourse(options = {}) {
  return useMutation({
    mutationFn: ({ id, ...data }) => api.courses.archive(id, data),
    onSuccess: () => {
      invalidateQueries.courses();
      invalidateQueries.students();
    },
    ...options,
  });
}

// Unarchive course roster mutation
export function useUnarchiveCourse(options = {}) {
  return useMutation({
    mutationFn: ({ id, ...data }) => api.courses.unarchive(id, data),
    onSuccess: () => {
      invalidateQueries.courses();
      invalidateQueries.students();
    },
    ...options,
  });
}

// Dismiss "ready to archive" nudge mutation
export function useDismissArchiveNudge(options = {}) {
  return useMutation({
    mutationFn: api.courses.dismissArchiveNudge,
    onSuccess: () => {
      invalidateQueries.courses();
    },
    ...options,
  });
}

// Hook for active courses (commonly used for dropdowns)
export function useActiveCourses(options = {}) {
  return useCourses({ is_active: true }, {
    staleTime: 10 * 60 * 1000, // 10 minutes - active courses change infrequently
    select: (data) => data.results || [],
    ...options,
  });
}

export default useCourses;